import React, { useState } from 'react';
import { API_BASE } from '../../config';

import { useEscapeToClose } from '../../hooks/useEscapeToClose';
import { useTurnstile } from '../../hooks/useTurnstile';
/**
 * Pre-launch gate modal — shown on healist.ai / www.healist.ai when
 * PRE_LAUNCH_MODE=true. Intercepts the Sign Up click only.
 *
 * Sign In still works normally (credentials + MFA are the security boundary), so existing
 * beta testers are never blocked by this modal.
 *
 * WHAT THIS MODAL NO LONGER DOES (changed 2026-08-03, Vincent):
 * It used to capture an email here and POST it to /api/auth/waitlist WITHOUT a Turnstile token.
 * That token-less caller is precisely why the waitlist endpoint decided whether to bot-check
 * based on the client-supplied `source_page` field — a string an attacker just edits, which
 * turned the endpoint into an open email relay. The fix was to delete the second capture surface
 * rather than bolt a second Turnstile widget on: the /beta page is now the ONLY place an email
 * enters the waitlist, and it is Turnstile-protected. This modal points people there.
 * => Do NOT reintroduce a waitlist POST here. One door, one lock.
 *
 * Two modes:
 *  - invited=true  (arrived via ?invite=1): "Welcome to the Beta" — confirm the invited address
 *    and go straight to account setup.
 *  - invited=false (clicked Sign Up cold): closed-beta explanation + a CTA to /beta, with an
 *    "already invited" path for someone who has an invite but came without the link.
 */
function PreLaunchModal({ onClose, onBetaAllowed, invited = false }) {
  useEscapeToClose(onClose);   // Escape dismisses this modal
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [notRecognised, setNotRecognised] = useState(false);
  const [throttled, setThrottled] = useState(false);
  // Non-invited visitors start on the CTA; the email field is revealed only if they say they
  // already have an invite. Keeps the default path a single obvious action, and stops every
  // casual visitor from probing /api/auth/beta-check on their way past.
  const [showEmailEntry, setShowEmailEntry] = useState(invited);

  // Turnstile gates beta-check. This endpoint answers "is this exact address a beta invitee?",
  // so unprotected it lets a script probe a list of addresses to learn who is a Healist tester.
  // The token is what makes that expensive: a script cannot mint one, so the server grants a
  // token-less caller only a tight probe budget. It is NOT an access gate — a real invited person
  // whose check degrades still gets through (see useTurnstile's degrade-never-dead-end contract).
  const turnstile = useTurnstile();

  const goToBeta = () => { window.location.href = '/beta'; };

  const handleSubmit = async () => {
    if (!email || !email.includes('@')) return;
    setLoading(true);
    setNotRecognised(false);
    setThrottled(false);

    // #83 Beta gate — an allowlisted email is advanced into the REAL signup form.
    try {
      const res = await fetch(`${API_BASE}/api/auth/beta-check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Send whatever we have. '' when Turnstile is off (Dev) or degraded — the SERVER decides
        // what that is worth. The client never withholds the request over a missing token.
        body: JSON.stringify({ email, turnstile_token: turnstile.token || null }),
      });
      // 429 = the per-IP probe throttle. Say so plainly rather than rendering it as
      // "we don't recognise you", which would misinform a legitimate invited user.
      if (res.status === 429) {
        setLoading(false);
        setThrottled(true);
        turnstile.reset();   // tokens are single-use; re-challenge before they try again
        return;
      }
      const data = await res.json();
      if (data && data.allowed && typeof onBetaAllowed === 'function') {
        setLoading(false);
        onBetaAllowed(email);   // parent closes the modal + switches to the register form
        return;
      }
      // Address is not on the allowlist — almost always a typo, or a different address than the
      // invite went to. Say so plainly instead of silently dumping them somewhere, which would
      // read as "your invite is fake".
      setLoading(false);
      setNotRecognised(true);
      turnstile.reset();     // the token we just spent is consumed — issue a fresh challenge
    } catch (_) {
      // beta-check unreachable — never dead-end the person. /beta works independently of this
      // call, so surfacing that route beats a dead spinner or a silent failure.
      setLoading(false);
      setNotRecognised(true);
      turnstile.reset();
    }
  };

  return (
    <div className="modal-overlay pre-launch-modal">
      <div className="modal-content modal-content-sm">
        <div className="pre-launch-header">
          <div className="pre-launch-logo">Healist<span>.AI</span></div>
          <h3 className="modal-title modal-title-center">
            {invited ? 'Welcome to the Beta' : 'Healist.AI is in Closed Beta'}
          </h3>
        </div>

        {invited ? (
          <>
            <p className="modal-subtitle" style={{ textAlign: 'center' }}>
              Enter the email address your invitation was sent to and we'll take you
              straight to account setup.
            </p>
            {/* Third and final statement of the iOS caveat (after /beta and the invite email).
                Repetition is deliberate: this is the last screen before someone invests time in
                setup, and an invitee who arrived via a forwarded link may have seen neither of
                the others. Cheaper to say it once more than to have a tester conclude the device
                features are broken. */}
            <p className="pre-launch-ios-note">
              <strong>Best on iPhone</strong> &mdash; device &amp; wearable syncing runs through
              <strong> HealistHealthSync v1.0</strong> (free, App Store) via Apple Health.
              <strong> v2.0</strong>, which adds your clinical health records, is with Apple for
              review now; an Android companion app is on the way. Everything else works on Android.
            </p>
          </>
        ) : (
          <>
            <p className="modal-subtitle" style={{ textAlign: 'center' }}>
              We're opening Healist.AI to a small group of beta testers, in batches. Request an
              invite and we'll be in touch as places become available.
              <br /><strong>Free access for 60 days &mdash; no card required.</strong>
            </p>
            {/* The device caveat used to render ONLY in the `invited` branch, so a cold visitor
                clicking Sign Up never saw it here — they met it only if they continued to /beta.
                First touch should carry it: this is the beta cohort's single most consequential
                fact. */}
            <p className="pre-launch-ios-note">
              <strong>Best on iPhone</strong> &mdash; device &amp; wearable syncing runs through
              <strong> HealistHealthSync v1.0</strong> (free, App Store) via Apple Health.
              <strong> v2.0</strong>, which adds your clinical health records, is with Apple for
              review now; an Android companion app is on the way. Everything else works on Android.
            </p>
          </>
        )}

        {/* Primary path for a cold visitor: go to the one real invite-request page. */}
        {!invited && !showEmailEntry && (
          <div className="pre-launch-waitlist">
            <button type="button" className="btn btn-primary" onClick={goToBeta}>
              Request a Beta Invite
            </button>
            <p className="modal-subtitle" style={{ textAlign: 'center', marginTop: '14px' }}>
              Already invited?{' '}
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); setShowEmailEntry(true); }}
              >
                Enter your email
              </a>
            </p>
          </div>
        )}

        {showEmailEntry && (
          <div className="pre-launch-waitlist">
            <p className="pre-launch-waitlist-label">Your invited email address:</p>
            <div className="form-group">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
              />
            </div>
            {notRecognised && (
              <p className="modal-subtitle" style={{ textAlign: 'center', color: '#8a1c1c' }}>
                We don't recognise that address. Please use the exact email your invitation
                was sent to &mdash; or{' '}
                <a href="#" onClick={(e) => { e.preventDefault(); goToBeta(); }}>
                  request an invite
                </a>.
              </p>
            )}
            {throttled && (
              <p className="modal-subtitle" style={{ textAlign: 'center', color: '#8a1c1c' }}>
                Too many attempts from this connection. Please wait a few minutes and try again.
              </p>
            )}
            {/* Turnstile renders here on Prod. On Dev the hook builds nothing, so this div stays
                empty and the layout is identical to the pre-Turnstile modal. */}
            <div ref={turnstile.mountRef}></div>
            <button
              type="button"
              className="btn btn-primary"
              // `ready` is true on Dev, once a token arrives, OR once the check has degraded —
              // so this button can never be permanently stuck waiting on Cloudflare.
              disabled={loading || !email.includes('@') || !turnstile.ready}
              onClick={handleSubmit}
            >
              {loading ? <span className="spinner"></span> : 'Continue to Account Setup'}
            </button>
          </div>
        )}

        <div className="pre-launch-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}

export default PreLaunchModal;
